/**
 * Phase 1: Incremental Document Monitoring Hook
 *
 * ARCHITECTURE:
 * - Frontend detection only (never enforces)
 * - All decisions deferred to backend via /monitor/face-verdict
 * - MediaPipe loads ONLY when viewer opens (lazy initialization)
 *
 * POLICY (Phase 1 - Face Count):
 * - warningAt: 2 faces
 * - lockAt: 4 faces
 * - consecutiveRequired: 2 consecutive detections to trigger POST
 */

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/config/api';
import { faceDetectionService } from '@/utils/faceDetection';

function captureSnapshotFromWebcam(webcamRef) {
  try {
    const video = webcamRef?.current?.video;
    if (!video || !video.videoWidth || !video.videoHeight) return [];

    const vw = video.videoWidth;
    const vh = video.videoHeight;

    const canvas = document.createElement('canvas');
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return [];

    ctx.drawImage(video, 0, 0, vw, vh);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image')) {
      return [dataUrl];
    }
    return [];
  } catch {
    return [];
  }
}

const usePhase1Monitor = ({
  enabled,
  webcamRef,
  udtId,
  token,
  monitorSessionId,
  onAction
}) => {
  // State
  const [faceCount, setFaceCount] = useState(-1);
  const [status, setStatus] = useState('idle'); // idle | monitoring | warning | locked
  
  // Refs for monitoring
  const monitoringInterval = useRef(null);
  const lastSnapshotRef = useRef(null);
  const consecutiveRef = useRef(0);
  const lastVerdictTime = useRef(0);
  const lastEventTypeRef = useRef(null);
  const mediapikeReadyRef = useRef(false);
  const tabSwitchCountRef = useRef(0);
  const actionRef = useRef(null);
  const isLockedRef = useRef(false);
  const TAB_LOCK_AT = 3; // lock after 3 tab switches

  // Policy constants
  const POLICY = {
    warningAt: 2,
    lockAt: 4,
    consecutiveRequired: 2
  };

  const THROTTLE_MS = 2000; // Minimum interval between POST requests

  /**
   * Check faces and potentially send verdict to backend
   */
  const checkFaces = async () => {
    if (!webcamRef?.current || !enabled) return;

    try {
      // Detect faces using MediaPipe
      const result = await faceDetectionService.detectFaces(
        webcamRef.current.video,
        Date.now()
      );

      const { faceCount: detectedCount } = result;

      // DEBUG: Log face detection state
      console.log('[Phase1] Face count:', detectedCount, '| MediaPipe state:', faceDetectionService.getState());

      // If faceCount is -1, MediaPipe not ready - skip silently
      if (detectedCount < 0) {
        setFaceCount(-1);
        setStatus('idle');
        return;
      }

      setFaceCount(detectedCount);

      // Best-effort: keep the latest good snapshot cached for proof (used for tab switch, etc.)
      try {
        const snapArr = captureSnapshotFromWebcam(webcamRef);
        if (Array.isArray(snapArr) && snapArr[0]) {
          lastSnapshotRef.current = snapArr[0];
        }
      } catch {
        // ignore snapshot failures
      }

      // Check if face count is normal (0 or 1)
      if (detectedCount <= 1) {
        // Normal state - reset consecutive counter
        consecutiveRef.current = 0;
        setStatus('idle');
        return;
      }

      // Abnormal face count (2+) - increment consecutive counter
      consecutiveRef.current++;

      // Determine event type based on policy
      let eventType = null;
      let actionNeeded = false;

      if (detectedCount >= POLICY.lockAt) {
        // 4+ faces - HIGH RISK
        eventType = 'MULTIPLE_FACES_LOCAL';
        // POST immediately on detecting 4+ faces (don't wait for consecutive threshold)
        actionNeeded = true;
        consecutiveRef.current = POLICY.consecutiveRequired; // Mark as ready
      } else if (detectedCount >= POLICY.warningAt) {
        // 2-3 faces - WARNING level
        eventType = 'MULTIPLE_FACES_WARNING';
        // Wait for consecutiveRequired detections
        actionNeeded = consecutiveRef.current >= POLICY.consecutiveRequired;
      }

      const now = Date.now();

      if (actionNeeded && eventType) {
        // DEBUG: explicit log before POST
        console.log(`[Phase1] 🚨 VIOLATION: ${detectedCount} faces. POSTing to backend as ${eventType}...`);
      }

      // Only POST if:
      // 1. We have an actionable event type
      // 2. Enough time has passed since last POST (throttle)
      // 3. Event type has changed (avoid duplicate posts)
      if (
        actionNeeded &&
        eventType &&
        (now - lastVerdictTime.current) >= THROTTLE_MS &&
        eventType !== lastEventTypeRef.current
      ) {
        await postFaceVerdict(detectedCount, eventType);
        lastVerdictTime.current = now;
        lastEventTypeRef.current = eventType;
      }
    } catch (error) {
      console.error('[Phase1Monitor] Face check error:', error.message);
      setStatus('idle');
    }
  };

  /**
   * POST face detection verdict to backend
   */
  const postFaceVerdict = async (detectedCount, eventType) => {
    try {
      setStatus('warning');

      const croppedImages =
        lastSnapshotRef.current ? [lastSnapshotRef.current] : captureSnapshotFromWebcam(webcamRef);

      const payload = {
        monitor_session_id: monitorSessionId,
        udt_id: udtId,
        token: token,
        event_type: eventType,
        mediapipe_metadata: {
          face_count: detectedCount,
          policy_warning_at: POLICY.warningAt,
          policy_lock_at: POLICY.lockAt
        },
        cropped_images: croppedImages,
        timestamp_ms: Date.now()
      };

      const response = await axios.post(`${API}/monitor/face-verdict`, payload);
      const { action, reason } = response.data || {};

      console.log('[Phase1Monitor] Action:', action, '| Reason:', reason);

      // Call onAction callback
      if (onAction) {
        onAction({
          action,
          reason,
          faceCount: detectedCount,
          eventType
        });
      }

      // Update status based on backend action
      if (action === 'lock') {
        setStatus('locked');
        actionRef.current = 'locked';
      } else if (action === 'warn') {
        setStatus('warning');
      } else {
        setStatus('idle');
      }
    } catch (error) {
      console.error('[Phase1Monitor] Verdict POST failed:', error.message);
      setStatus('idle');
      // Don't throw - let monitoring continue even if backend call fails
    }
  };

  /**
   * Start monitoring loop
   */
  const startMonitoring = () => {
    if (monitoringInterval.current) return; // Already running

    monitoringInterval.current = setInterval(() => {
      checkFaces();
    }, 2000); // Check every 2000ms
  };

  /**
   * Stop monitoring loop
   */
  const stopMonitoring = () => {
    if (monitoringInterval.current) {
      clearInterval(monitoringInterval.current);
      monitoringInterval.current = null;
    }
    consecutiveRef.current = 0;
    lastEventTypeRef.current = null;
    lastVerdictTime.current = 0;
    setStatus('idle');
  };

  /**
   * Start monitoring when enabled
   * Initialize MediaPipe ONCE, then start polling for faces
   */
  useEffect(() => {
    console.log('[Phase1] useEffect triggered. enabled:', enabled, 'sessionId:', monitorSessionId);
    
    if (!enabled || !monitorSessionId) {
      console.warn('[Phase1] NOT starting:', {enabled, monitorSessionId});
      if (monitoringInterval.current) {
        clearInterval(monitoringInterval.current);
        monitoringInterval.current = null;
      }
      return;
    }

    console.log('[Phase1] ✅ Starting interval');

    // Initialize MediaPipe ONCE (will begin loading async)
    faceDetectionService.initialize();

    // Start polling immediately (will return -1 until MediaPipe is ready)
    monitoringInterval.current = setInterval(() => {
      checkFaces();
    }, 2000);

    // Cleanup: stop interval when disabled
    return () => {
      if (monitoringInterval.current) {
        clearInterval(monitoringInterval.current);
        monitoringInterval.current = null;
      }
    };
  }, [enabled, monitorSessionId]);

  // Pre-emptive snapshot: when the mouse leaves the window or the window blurs,
  // try to cache a fresh webcam snapshot so that a subsequent TAB_SWITCH_VIOLATION
  // has a recent image even if the tab is already hidden when visibilitychange fires.
  useEffect(() => {
    if (!enabled || !monitorSessionId) return;

    const handlePreemptiveSnapshot = () => {
      try {
        const snapArr = captureSnapshotFromWebcam(webcamRef);
        if (Array.isArray(snapArr) && snapArr[0]) {
          lastSnapshotRef.current = snapArr[0];
        }
      } catch {
        // ignore
      }
    };

    window.addEventListener('blur', handlePreemptiveSnapshot);
    window.addEventListener('mouseleave', handlePreemptiveSnapshot);

    return () => {
      window.removeEventListener('blur', handlePreemptiveSnapshot);
      window.removeEventListener('mouseleave', handlePreemptiveSnapshot);
    };
  }, [enabled, monitorSessionId, webcamRef]);

  useEffect(() => {
    if (!enabled || !monitorSessionId) return;

    const handleTabSwitch = async () => {
      if (isLockedRef.current) {
        console.log('[Phase1] Document locked, ignoring event');
        return;
      }
      if (actionRef.current === 'locked') {
        console.log('[Phase1] Already locked, ignoring further events');
        return;
      }
      if (!document.hidden) return;

      tabSwitchCountRef.current += 1;
      const count = tabSwitchCountRef.current;

      console.log(`[Phase1] Tab switch ${count}/${TAB_LOCK_AT}`);

      if (count >= TAB_LOCK_AT) {
        console.log(`[Phase1] 🚨 LOCK THRESHOLD REACHED (${count}). Sending to backend...`);

        try {
          const croppedImages =
            lastSnapshotRef.current ? [lastSnapshotRef.current] : captureSnapshotFromWebcam(webcamRef);

          const payload = {
            monitor_session_id: monitorSessionId,
            udt_id: udtId,
            token,
            event_type: 'TAB_SWITCH_VIOLATION',
            mediapipe_metadata: {
              tab_switch_count: count,
              lock_at: TAB_LOCK_AT,
            },
            cropped_images: croppedImages,
            timestamp_ms: Date.now(),
          };

          console.log('[Phase1] POST payload:', payload);

          const resp = await axios.post(`${API}/monitor/face-verdict`, payload);

          console.log('[Phase1] Backend response:', resp.status, resp.data);

          if (resp.data?.action === 'lock' || resp.data?.action === 'revoke') {
            console.log('[Phase1] Lock action received — calling onAction');
            isLockedRef.current = true;
            onAction?.({ ...resp.data, action: 'lock' });
            return;
          } else {
            console.warn('[Phase1] Expected lock but got:', resp.data?.action);
          }
        } catch (err) {
          console.error('[Phase1] POST FAILED:', err.response?.status, err.response?.data || err.message);
          if (err?.response?.status === 403) {
            isLockedRef.current = true;
            onAction?.({ action: 'lock', reason: 'Access revoked' });
          }
        }
      } else {
        // Just warn
        onAction?.({
          action: 'warn',
          reason: `Tab switched ${count}/${TAB_LOCK_AT} times.`,
        });
      }
    };

    document.addEventListener('visibilitychange', handleTabSwitch);
    return () => {
      document.removeEventListener('visibilitychange', handleTabSwitch);
      // BUG 3 FIX: DO NOT reset count on cleanup - it persists across re-renders
      // When monitorSessionId changes, listener re-registers but count must NOT reset
      // tabSwitchCountRef.current = 0; // ← REMOVED
    };
  }, [enabled, monitorSessionId, udtId, token, onAction]);

  // Return hook API
  return {
    faceCount,
    status,
    // For testing/debugging only
    _consecutive: consecutiveRef.current,
    _policy: POLICY
  };
}

export default usePhase1Monitor;
